import React, { useEffect, useState } from "react";

import { api } from "../lib/api";
import { alertMonthLabel, monthsUntilContractEnd } from "../lib/contract";
import { opportunityFormSchema } from "../schemas/opportunityForm";
import {
  Channel,
  Company,
  CompanySelection,
  Contact,
  ContactSelection,
  Opportunity,
  Pipeline,
  PipelineStage,
  Product,
  UserMini,
} from "../types/crm";
import { CompanyPicker } from "./CompanyPicker";
import { ContactPicker } from "./ContactPicker";

interface OpportunityFormModalProps {
  opportunity?: Opportunity;
  companies: Company[];
  channels: Channel[];
  products: Product[];
  pipelines: Pipeline[];
  users: UserMini[];
  onClose: () => void;
  onSaved: (opportunity: Opportunity) => void;
}

const FAROL_OPTIONS = [
  {
    id: "verde",
    label: "Verde",
    dot: "#22C55E",
    activeBg: "#DCFCE7",
    activeText: "#166534",
    desc: "Cliente saudável, renovação provável",
  },
  {
    id: "amarelo",
    label: "Amarelo",
    dot: "#F59E0B",
    activeBg: "#FEF3C7",
    activeText: "#92400E",
    desc: "Atenção — engajamento caindo",
  },
  {
    id: "vermelho",
    label: "Vermelho",
    dot: "#EF4444",
    activeBg: "#FEE2E2",
    activeText: "#DC2626",
    desc: "Risco de churn, ação imediata",
  },
] as const;

function defaultCompanySelection(opportunity?: Opportunity, companies?: Company[]): CompanySelection {
  if (opportunity) return { type: "existing", id: opportunity.company };
  if (companies && companies.length > 0) return { type: "existing", id: companies[0].id };
  return { type: "new", data: { name: "", cnpj: "", website: "" } };
}

function defaultContactSelection(opportunity?: Opportunity): ContactSelection {
  if (opportunity) return { type: "existing", id: opportunity.primary_contact };
  return { type: "new", data: { name: "", email: "", phone: "", role: "" } };
}

export function OpportunityFormModal({
  opportunity,
  companies,
  channels,
  products,
  pipelines,
  users,
  onClose,
  onSaved,
}: OpportunityFormModalProps) {
  const [companySelection, setCompanySelection] = useState<CompanySelection>(
    defaultCompanySelection(opportunity, companies)
  );
  const [contactSelection, setContactSelection] = useState<ContactSelection>(
    defaultContactSelection(opportunity)
  );
  const [ownerId, setOwnerId] = useState<number | undefined>(opportunity?.owner);
  const [channelId, setChannelId] = useState<number | undefined>(opportunity?.channel ?? undefined);

  const defaultPipelineId =
    opportunity?.pipeline_detail?.id ??
    pipelines.find((p) => p.is_default)?.id ??
    pipelines[0]?.id;
  const [pipelineId, setPipelineId] = useState<number | undefined>(defaultPipelineId);
  const [stages, setStages] = useState<PipelineStage[]>([]);
  const [stageId, setStageId] = useState<number | undefined>(opportunity?.stage);

  const [productIds, setProductIds] = useState<number[]>(opportunity?.products ?? []);
  const [mrrEstimated, setMrrEstimated] = useState(opportunity?.mrr_estimated ?? "0");
  const [nonRecurringEstimated, setNonRecurringEstimated] = useState(
    opportunity?.non_recurring_estimated ?? "0"
  );
  const [resultValue, setResultValue] = useState<"ganho" | "perdido" | "">(
    opportunity?.result ?? ""
  );
  const [meetingDate, setMeetingDate] = useState("");
  const [contractEnd, setContractEnd] = useState(opportunity?.contract_end ?? "");
  const [farolValue, setFarolValue] = useState<"verde" | "amarelo" | "vermelho" | "">(
    opportunity?.farol ?? ""
  );
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedPipeline = pipelines.find((p) => p.id === pipelineId);
  const selectedStage = stages.find((s) => s.id === stageId);

  // Carrega as etapas do pipeline escolhido; ao trocar de funil, seleciona a
  // primeira etapa (a menos que a oportunidade editada já esteja numa etapa dele).
  useEffect(() => {
    if (!selectedPipeline) return;
    let cancelled = false;
    api
      .get<PipelineStage[]>(`/pipeline-stages/?pipeline=${selectedPipeline.slug}`)
      .then((response) => {
        if (cancelled) return;
        setStages(response.data);
        setStageId((current) => {
          if (current && response.data.some((s) => s.id === current)) return current;
          return response.data.find((s) => s.order === 1)?.id ?? response.data[0]?.id;
        });
      })
      .catch(() => {
        if (!cancelled) setStages([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedPipeline]);

  const toggleProduct = (productId: number) => {
    setProductIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  };

  const monthsLeft = contractEnd ? monthsUntilContractEnd(contractEnd) : null;
  const contractAviso = monthsLeft !== null && monthsLeft <= 1;

  const contractHelp = (() => {
    if (!contractEnd)
      return { text: "O sistema avisa 1 mês antes do vencimento.", tone: "gray" as const };
    if (monthsLeft === null) return { text: "", tone: "gray" as const };
    if (monthsLeft < 0)
      return { text: "Contrato já vencido — priorize a renovação.", tone: "red" as const };
    if (monthsLeft === 0)
      return { text: "Vence este mês — alerta ativo em Follow-ups.", tone: "amber" as const };
    if (monthsLeft === 1)
      return {
        text: "Vence no próximo mês — alerta ativo em Follow-ups.",
        tone: "amber" as const,
      };
    return {
      text: `Faltam ${monthsLeft} meses. Alerta dispara em ${alertMonthLabel(contractEnd)}.`,
      tone: "gray" as const,
    };
  })();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setFormError(null);

    const parsed = opportunityFormSchema.safeParse({
      owner: ownerId,
      channel: channelId,
      stage: stageId,
      mrr_estimated: Number(mrrEstimated),
      non_recurring_estimated: Number(nonRecurringEstimated),
    });

    const errors: Record<string, string> = {};
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      for (const key of Object.keys(flat)) {
        errors[key] = flat[key as keyof typeof flat]?.[0] ?? "Campo inválido";
      }
    }
    if (companySelection.type === "new" && !companySelection.data.name.trim()) {
      errors.company = "Informe o nome da empresa";
    }
    if (contactSelection.type === "new" && !contactSelection.data.name.trim()) {
      errors.contact = "Informe o nome do contato";
    }
    if (selectedStage?.requires_meeting_date && !meetingDate) {
      errors.meeting_date = "Informe a data da reunião";
    }
    if (selectedStage?.is_closed && !selectedStage?.is_implementation && !resultValue) {
      errors.result = "Selecione o resultado";
    }
    if (selectedPipeline?.has_contract && !contractEnd) {
      errors.contract_end = "Informe o mês de finalização do contrato";
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setIsSubmitting(true);
    try {
      const companyId =
        companySelection.type === "existing"
          ? companySelection.id
          : (await api.post<Company>("/companies/", companySelection.data)).data.id;

      const contactId =
        contactSelection.type === "existing"
          ? contactSelection.id
          : (
              await api.post<Contact>("/contacts/", {
                ...contactSelection.data,
                company: companyId,
              })
            ).data.id;

      const payload = {
        company: companyId,
        primary_contact: contactId,
        owner: ownerId,
        channel: channelId,
        stage: stageId,
        products: productIds,
        mrr_estimated: parsed.data!.mrr_estimated,
        non_recurring_estimated: parsed.data!.non_recurring_estimated,
        needs_completion: false,
        contract_end: selectedPipeline?.has_contract ? contractEnd : "",
        farol: selectedPipeline?.has_farol ? farolValue || null : null,
        ...(selectedStage?.requires_meeting_date ? { meeting_date: meetingDate } : {}),
        ...(selectedStage?.is_closed && !selectedStage?.is_implementation
          ? { result: resultValue }
          : {}),
      };

      const response = opportunity
        ? await api.patch<Opportunity>(`/opportunities/${opportunity.id}/`, payload)
        : await api.post<Opportunity>("/opportunities/", payload);

      onSaved(response.data);
    } catch {
      setFormError("Não foi possível salvar a oportunidade. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClass =
    "mt-1 block w-full rounded-[10px] border border-border-subtle px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary";

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-black/30 py-8">
      <div className="w-full max-w-lg rounded-[10px] border border-border-subtle bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">
            {opportunity ? "Editar oportunidade" : "Nova oportunidade"}
          </h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            ✕
          </button>
        </div>

        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div>
            <CompanyPicker companies={companies} value={companySelection} onChange={setCompanySelection} />
            {fieldErrors.company && <p className="mt-1 text-sm text-red-600">{fieldErrors.company}</p>}
          </div>

          <div>
            <ContactPicker
              companySelection={companySelection}
              value={contactSelection}
              onChange={setContactSelection}
            />
            {fieldErrors.contact && <p className="mt-1 text-sm text-red-600">{fieldErrors.contact}</p>}
          </div>

          <div>
            <label htmlFor="owner" className="block text-sm font-medium text-slate-700">
              Responsável
            </label>
            <select id="owner" value={ownerId ?? ""} onChange={(e) => setOwnerId(Number(e.target.value))} className={selectClass}>
              <option value="" disabled>
                Selecione um responsável
              </option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.username}
                </option>
              ))}
            </select>
            {fieldErrors.owner && <p className="mt-1 text-sm text-red-600">{fieldErrors.owner}</p>}
          </div>

          <div>
            <label htmlFor="channel" className="block text-sm font-medium text-slate-700">
              Canal
            </label>
            <select id="channel" value={channelId ?? ""} onChange={(e) => setChannelId(Number(e.target.value))} className={selectClass}>
              <option value="" disabled>
                Selecione um canal
              </option>
              {channels.map((channel) => (
                <option key={channel.id} value={channel.id}>
                  {channel.name}
                </option>
              ))}
            </select>
            {fieldErrors.channel && <p className="mt-1 text-sm text-red-600">{fieldErrors.channel}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="pipeline" className="block text-sm font-medium text-slate-700">
                Pipeline
              </label>
              <select
                id="pipeline"
                value={pipelineId ?? ""}
                onChange={(e) => setPipelineId(Number(e.target.value))}
                className={selectClass}
              >
                {pipelines.map((pipeline) => (
                  <option key={pipeline.id} value={pipeline.id}>
                    {pipeline.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="stage" className="block text-sm font-medium text-slate-700">
                Etapa
              </label>
              <select id="stage" value={stageId ?? ""} onChange={(e) => setStageId(Number(e.target.value))} className={selectClass}>
                <option value="" disabled>
                  Selecione uma etapa
                </option>
                {stages.map((stage) => (
                  <option key={stage.id} value={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
              {fieldErrors.stage && <p className="mt-1 text-sm text-red-600">{fieldErrors.stage}</p>}
            </div>
          </div>

          {selectedPipeline?.has_contract && (
            <div className="rounded-[10px] border border-border-subtle bg-surface p-4">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-slate-700">Contrato vigente</h3>
                <span className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-primary">
                  Renovações
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contract_end" className="block text-sm font-medium text-slate-700">
                    Mês de finalização do contrato
                  </label>
                  <input
                    id="contract_end"
                    type="month"
                    value={contractEnd}
                    onChange={(e) => setContractEnd(e.target.value)}
                    className="mt-1 block w-full rounded-[10px] border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
                    style={
                      contractAviso
                        ? { borderColor: "#FDE68A", backgroundColor: "#FFFBEB" }
                        : { borderColor: "#E2E8F0" }
                    }
                  />
                  <p
                    className="mt-1 text-xs"
                    style={{
                      color:
                        contractHelp.tone === "amber"
                          ? "#92400E"
                          : contractHelp.tone === "red"
                          ? "#DC2626"
                          : "#94A3B8",
                    }}
                  >
                    {contractHelp.text}
                  </p>
                  {fieldErrors.contract_end && (
                    <p className="mt-1 text-sm text-red-600">{fieldErrors.contract_end}</p>
                  )}
                </div>

                {selectedPipeline?.has_farol && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700">Farol do cliente</label>
                    <div className="mt-1 flex gap-2">
                      {FAROL_OPTIONS.map((opt) => {
                        const active = farolValue === opt.id;
                        return (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setFarolValue(active ? "" : opt.id)}
                            className="flex flex-1 items-center justify-center gap-1 rounded-[10px] px-2 py-2 text-xs"
                            style={
                              active
                                ? {
                                    backgroundColor: opt.activeBg,
                                    color: opt.activeText,
                                    border: `1.5px solid ${opt.dot}`,
                                    fontWeight: 600,
                                  }
                                : {
                                    backgroundColor: "#F8FAFC",
                                    border: "1px solid #E2E8F0",
                                    color: "#64748B",
                                  }
                            }
                          >
                            <span
                              className="inline-block h-2 w-2 rounded-full"
                              style={{ backgroundColor: opt.dot }}
                            />
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-1 text-xs text-slate-400">
                      {FAROL_OPTIONS.find((o) => o.id === farolValue)?.desc ??
                        "Selecione o status de saúde do cliente"}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedStage?.requires_meeting_date && (
            <div>
              <label htmlFor="meeting_date" className="block text-sm font-medium text-slate-700">
                Data da reunião
              </label>
              <input
                id="meeting_date"
                type="date"
                value={meetingDate}
                onChange={(e) => setMeetingDate(e.target.value)}
                className={selectClass}
              />
              {fieldErrors.meeting_date && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.meeting_date}</p>
              )}
            </div>
          )}

          {selectedStage?.is_closed && !selectedStage?.is_implementation && (
            <div>
              <label className="block text-sm font-medium text-slate-700">Resultado</label>
              <div className="mt-1 space-y-1">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="radio" name="result" checked={resultValue === "ganho"} onChange={() => setResultValue("ganho")} />
                  Ganho
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="radio" name="result" checked={resultValue === "perdido"} onChange={() => setResultValue("perdido")} />
                  Perdido
                </label>
              </div>
              {fieldErrors.result && <p className="mt-1 text-sm text-red-600">{fieldErrors.result}</p>}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700">Produtos</label>
            <div className="mt-1 flex flex-wrap gap-3">
              {products.map((product) => (
                <label key={product.id} className="flex items-center gap-1 text-sm text-slate-700">
                  <input type="checkbox" checked={productIds.includes(product.id)} onChange={() => toggleProduct(product.id)} />
                  {product.name}
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="mrr_estimated" className="block text-sm font-medium text-slate-700">
                MRR estimado
              </label>
              <input id="mrr_estimated" type="number" min="0" step="0.01" value={mrrEstimated} onChange={(e) => setMrrEstimated(e.target.value)} className={selectClass} />
              {fieldErrors.mrr_estimated && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.mrr_estimated}</p>
              )}
            </div>
            <div>
              <label htmlFor="non_recurring_estimated" className="block text-sm font-medium text-slate-700">
                Não recorrente
              </label>
              <input id="non_recurring_estimated" type="number" min="0" step="0.01" value={nonRecurringEstimated} onChange={(e) => setNonRecurringEstimated(e.target.value)} className={selectClass} />
              {fieldErrors.non_recurring_estimated && (
                <p className="mt-1 text-sm text-red-600">{fieldErrors.non_recurring_estimated}</p>
              )}
            </div>
          </div>

          {formError && <p className="text-sm text-red-600">{formError}</p>}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-[10px] px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface">
              Cancelar
            </button>
            <button type="submit" disabled={isSubmitting} className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-60">
              {isSubmitting ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
